import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'

// Un desplegable que busca en la API.
//
// Sirve para los controles de listado que devuelven una lista que crece sin
// filtro (los catalogos, los tipos de persona juridica, los terapeutas, los
// participantes): con un Select normal, la lista entera se descarga y se queda
// en pantalla.
//
// El comportamiento es el que se pidio:
//
//   - Al abrirlo, el listado inicial son los primeros 50 (el tope del
//     parametro TOP_RESULTADOS de la API) en orden alfabetico.
//   - Con tres o mas caracteres, el listado se refresca solo, con una pequena
//     espera para no preguntar en cada tecla.
//   - Con una o dos letras no se busca solo: se busca al pulsar Enter.
//
// Lo que devuelve el servidor son objetos { id, descripcion }.

export const LARGO_MINIMO_BUSQUEDA = 3

// La espera antes de preguntar al servidor: sin ella se pide una vez por
// tecla.
const ESPERA_BUSQUEDA = 300

const normalizar = (texto) => (texto || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const AutocompleteLista = (props) => {
    const {
        label,
        name,
        id,
        value,
        texto,
        cargar,
        onChange,
        disabled = false,
        size,
        required = false,
        placeholder = "",
        className,
        // Si se pone, el campo toma el ancho completo.
        fullWidth = true,
    } = props

    const [opciones, setOpciones] = useState([])
    const [buscando, setBuscando] = useState(false)
    const [textoInterno, setTextoInterno] = useState(texto == null ? "" : texto)

    // El temporizador de la busqueda: se cancela si el usuario sigue
    // escribiendo, para no preguntar al servidor por cada tecla.
    const temporizadorRef = useRef(null)

    // El padre recrea el callback en cada render: se guarda en un ref para
    // que los efectos no se disparen sin motivo.
    const cargarRef = useRef(cargar)
    cargarRef.current = cargar
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    // El texto que se propone al padre: sirve para distinguir lo que escribe
    // el usuario de lo que llega desde afuera.
    const textoPropuestoRef = useRef(texto == null ? "" : texto)

    // El ultimo texto que se escribio o se eligio. Los filtros que solo
    // guardan el id (no el texto) lo necesitan para mostrar lo elegido.
    const ultimoTextoRef = useRef(texto == null ? "" : texto)

    // Si el padre cambia el texto por su cuenta (el valor guardado que llega
    // del servidor), se vuelve al listado inicial.
    useEffect(() => {
        const t = texto == null ? "" : texto
        if (t !== textoPropuestoRef.current) {
            textoPropuestoRef.current = t
            ultimoTextoRef.current = t
            setTextoInterno(t)
        }
    }, [texto])

    // El listado inicial: los primeros 50 en orden alfabetico.
    const cargarInicial = useCallback(async () => {
        const buscar = cargarRef.current
        if (buscar == null) return

        setBuscando(true)
        try {
            const lista = await buscar("")
            setOpciones(Array.isArray(lista) ? lista : [])
        } catch (err) {
            console.log("Err: ", err)
            setOpciones([])
        } finally {
            setBuscando(false)
        }
    }, [])

    useEffect(() => {
        cargarInicial()
    }, [cargarInicial])

    // Buscar en el servidor.
    const buscarEnServidor = useCallback(async (textoBusqueda) => {
        const buscar = cargarRef.current
        if (buscar == null) return

        setBuscando(true)
        try {
            const lista = await buscar(textoBusqueda)
            setOpciones(Array.isArray(lista) ? lista : [])
        } catch (err) {
            console.log("Err: ", err)
            setOpciones([])
        } finally {
            setBuscando(false)
        }
    }, [])

    // Al escribir: con tres letras o mas se busca solo. Con una o dos no: se
    // espera al Enter.
    const onInputChange = useCallback((event, nuevoTexto, razon) => {
        const t = nuevoTexto == null ? "" : nuevoTexto
        setTextoInterno(t)
        textoPropuestoRef.current = t
        if (t.trim() !== '') ultimoTextoRef.current = t

        if (razon !== 'input') return

        if (t.trim() === "") {
            // Sin texto se vuelve al listado inicial: los primeros 50 en
            // orden alfabetico.
            if (temporizadorRef.current) clearTimeout(temporizadorRef.current)
            cargarInicial()
            return
        }

        if (t.trim().length >= LARGO_MINIMO_BUSQUEDA) {
            // Pequena espera para no pedir en cada tecla.
            if (temporizadorRef.current) clearTimeout(temporizadorRef.current)
            temporizadorRef.current = setTimeout(() => {
                buscarEnServidor(t)
            }, ESPERA_BUSQUEDA)
        }
    }, [buscarEnServidor, cargarInicial])

    // Al pulsar Enter se busca siempre, tambien con una o dos letras.
    const onKeyDown = useCallback((event) => {
        if (event.key !== 'Enter') return

        const t = textoInterno.trim()
        if (t.length === 0) return

        if (temporizadorRef.current) clearTimeout(temporizadorRef.current)
        buscarEnServidor(t)
    }, [textoInterno, buscarEnServidor])

    const opcionElegida = opciones.find((o) => o != null && o.id === value) || null

    // Lo que se ve en el campo: lo que se esta escribiendo, o lo del registro
    // elegido cuando el campo esta vacio (los filtros que solo guardan el id).
    const textoMostrado = textoInterno !== ""
        ? textoInterno
        : (ultimoTextoRef.current || (opcionElegida ? opcionElegida.descripcion : ""))

    return (
        <Autocomplete
            id={id}
            name={name}
            size={size}
            disabled={disabled}
            options={opciones}
            value={opcionElegida}
            inputValue={textoMostrado}
            onInputChange={onInputChange}
            onKeyDown={onKeyDown}
            getOptionLabel={(opcion) => (opcion == null ? "" : (opcion.descripcion || ""))}
            isOptionEqualToValue={(opcion, otro) => opcion != null && otro != null && opcion.id === otro.id}
            filterOptions={(x) => x}
            noOptionsText={buscando ? "Buscando..." : "Sin resultados"}
            loading={buscando}
            loadingText="Buscando..."
            openOnFocus
            fullWidth={fullWidth}
            className={className}
            onChange={(event, opcion) => {
                const idElegido = opcion != null ? opcion.id : 0
                const textoElegido = opcion != null ? (opcion.descripcion || "") : ""
                textoPropuestoRef.current = textoElegido
                setTextoInterno(textoElegido)
                ultimoTextoRef.current = textoElegido
                if (onChangeRef.current) onChangeRef.current(idElegido, textoElegido, opcion)
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    placeholder={placeholder}
                    inputProps={{
                        ...params.inputProps,
                        // El Enter va aqui y no en la prop onKeyDown del
                        // Autocomplete: MUI se queda con esa.
                        onKeyDown: onKeyDown,
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {buscando ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    )
}

export default AutocompleteLista
